import { EVC_ADDRESS } from '@/config/euler-contracts'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useReadContract } from 'wagmi'

export interface UseEnabledCollateralVaultsParams {
  /** The sub-account address to query enabled collaterals for. */
  accountAddress?: Address
  chainId: number
  enabled?: boolean
}

export interface UseEnabledCollateralVaultsResult {
  /** Lowercased vault addresses currently enabled as collateral on this account. */
  enabledVaults: string[]
  /** Raw vault addresses (not lowercased) as returned by the EVC. */
  rawVaults: Address[]
  isLoading: boolean
}

/**
 * Reads `EVC.getCollaterals(account)` to get the full set of vaults the user has
 * enabled as collateral on a specific sub-account.
 *
 * Why this over `accountLens.getAccountInfo(...).liquidityInfo.collaterals`?
 * The lens only returns collaterals that have a non-zero LTV against the provided
 * `liabilityVault`. That excludes perfectly-enabled collaterals whose LTV the
 * liability vault hasn't configured (or hasn't configured yet). For the card
 * Collateral-vs-Savings split we want the unfiltered enabled set, matching the
 * dashboard's Sub-account Positions view.
 */
export function useEnabledCollateralVaults({
  accountAddress,
  chainId,
  enabled = true,
}: UseEnabledCollateralVaultsParams): UseEnabledCollateralVaultsResult {
  const { data, isLoading } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'getCollaterals',
    args: accountAddress ? [accountAddress] : undefined,
    chainId,
    query: {
      enabled: enabled && !!accountAddress,
      staleTime: 30_000,
    },
  })

  const rawVaults = useMemo(() => (data as Address[] | undefined) ?? [], [data])
  const enabledVaults = useMemo(() => rawVaults.map((addr) => addr.toLowerCase()), [rawVaults])

  return {
    enabledVaults,
    rawVaults,
    isLoading: !!accountAddress && enabled && isLoading,
  }
}
